/*───────────────────────────────────────────────────────────────────────────*\
  File        : app/components/MagicLinkModal.tsx
  Description : Reusable modal that collects an e-mail address and triggers
                the password-less magic-link flow via userAuthApi.
\*───────────────────────────────────────────────────────────────────────────*/

"use client";

import type React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/app/components/Input";
import { Label } from "@/app/components/Label";
import { Mail, Loader2, CheckCircle } from "lucide-react";
import { sendMagicLink } from "@/src/utils/userAuthApi";

interface MagicLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MagicLinkModal({
  open,
  onOpenChange,
}: MagicLinkModalProps): JSX.Element {
  const [email, setEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSent, setIsSent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await sendMagicLink(email.trim());
      if (res.success) {
        setIsSent(true);
      } else {
        setError(res.error ?? "Failed to send magic link.");
      }
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Magic-link error:", err);
      /* eslint-enable no-console */
      setError("Failed to send magic link.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeAndReset = (): void => {
    if (isLoading) return;
    setEmail("");
    setIsSent(false);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={closeAndReset}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#40A1AB]" />
            Sign in with Magic Link
          </DialogTitle>
          <DialogDescription>
            Enter your e-mail address and weʼll send you a one-time sign-in link.
          </DialogDescription>
        </DialogHeader>

        {isSent ? (
          <div className="text-center space-y-4 py-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-sm">
              Weʼve sent a sign-in link to <strong>{email}</strong>. Check your inbox!
            </p>
            <Button onClick={closeAndReset} className="bg-[#40A1AB] hover:bg-[#3A919A] text-white">
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="magic-email">Email Address</Label>
              <Input
                id="magic-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {error && (
              <p className="text-sm text-red-600">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white"
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                "Send Magic Link"
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/*──────────────────────────── End of file ────────────────────────────────*/
